import { Link, useRoute } from "wouter";
import { useAuthStore } from "@/hooks/useAuthStore";
import { usePrimaryWallet } from "@/hooks/useWalletQueries";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { shortenAddress, getXDaoHomePath } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LogOut, Copy, X, ExternalLink, Menu, User } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import useSidebarStore from "@/stores/useSidebarStore";
import { copyWalletAddress } from "@/lib/walletUtils";
import IconChevronDown from "@/assets/icons/chevron_down.svg";
import IconChevronUp from "@/assets/icons/chevron_up.svg";
import IconWallet from "@/assets/icons/wallet.svg";
import IconDoorIn from "@/assets/icons/door_in.svg";
import useModalStore from "@/stores/useModalStore";
import AccountManagementModal from "@/components/AccountManagement/AccountManagementModal";

// Wallet Balance Component
interface WalletBalanceProps {
  publicKey: string;
}

function WalletBalance({ publicKey }: WalletBalanceProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['/api/v1/wallet/balance', publicKey],
    queryFn: async () => {
      if (!publicKey) return null;
      return apiRequest<{ publicKey: string; balanceLamports: number; balanceSol: number }>(
        'GET',
        `/api/v1/wallet/balance/${publicKey}`
      );
    },
    enabled: !!publicKey,
    refetchInterval: 60000, // Refetch every minute
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-black p-2 sm:p-3 rounded-lg border border-gray-800 h-[40px]">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        <span className="ml-2 text-xs sm:text-sm text-gray-400">Loading balance...</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-xs sm:text-sm bg-black p-2 sm:p-3 rounded-lg border border-gray-800 text-gray-400">
        Unable to fetch balance
      </p>
    );
  }

  return (
    <p className="text-xs sm:text-sm bg-black p-2 sm:p-3 rounded-lg border border-gray-800">
      {data.balanceSol} SOL{" "}
      <span className="text-gray-500 text-xs">({Number(data.balanceLamports).toLocaleString()} lamports)</span>
    </p>
  );
}

export default function Navbar() {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showWalletDetails, setShowWalletDetails] = useState(false);
  const { isAuthenticated, user, logout } = useAuthStore();
  const { data: primaryWalletData } = usePrimaryWallet();
  const primaryWallet = primaryWalletData?.wallet;
  const showSidebarWalletManagement = useSidebarStore((state) => state.setIsOpenWalletManagement);
  const setIsOpenDaoSidebar = useSidebarStore((state) => state.setIsOpenDaoSidebar);
  const { setLoginModal, setAccountManagementModal } = useModalStore();

  const handleLogout = async () => {
    // Track logout confirmation
    trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
      label: 'logout_confirm',
      component: 'navbar',
      location: 'logout_dialog'
    });

    await logout();
    setShowLogoutConfirm(false);
  };

  const openInSolanaExplorer = () => {
    if (primaryWallet?.publicKey) {
      window.open(`https://explorer.solana.com/address/${primaryWallet.publicKey}?cluster=devnet`, "_blank");
    }
  };

  const [sheetOpen, setSheetOpen] = useState(false);
  const [isDaoPartnerPage] = useRoute('/xdao/:slug');
  
  return (
    <nav className="px-4 py-3 md:px-8 flex justify-between items-center border-b border-gray-800">
      <div className="flex items-center">
        {/* DAO Sidebar Toggle - Only visible on mobile/tablet */}
        {isDaoPartnerPage && (
          <Button
            variant="ghost"
            size="icon"
            className="2xl:hidden mr-2 text-white"
            onClick={() => {
              // Track DAO sidebar open
              trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                label: 'open_dao_sidebar',
                component: 'navbar',
                location: 'mobile'
              });
              setIsOpenDaoSidebar(true);
            }}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Open DAO sidebar</span>
          </Button>
        )}

        <Link
          href={getXDaoHomePath()}
          className="flex items-center mr-8"
          aria-label="Tweetonium Home"
        >
          <Logo size="sm" />
        </Link>

        <Link
          href={getXDaoHomePath()}
          className="font-inter text-base text-white underline hidden md:block"
          aria-label="Tweetonium Home"
        >
          X-DAO
        </Link>

        {/* Desktop Navigation */}
        {/* <div className="ml-8 space-x-6 hidden md:flex">
          <Link href="/explore" className={`hover:text-gray-300 transition-colors ${location === "/explore" ? "font-medium" : ""}`}>
            Explore
          </Link>
          
          {isAuthenticated && (
            <Link href="/my-nfts" className={`hover:text-gray-300 transition-colors ${location === "/my-nfts" ? "font-medium" : ""}`}>
              My NFTs
            </Link>
          )}
        </div> */}        
      </div>

      <div>
        {!isAuthenticated ? (
          <button
            className="flex self-stretch justify-center items-center flex-row gap-2 py-3 px-6 border-solid border rounded-full shadow-[inset_0px_0px_8px_0px_rgba(84,49,131,0.6),0px_0px_0px_2px_#0F1D4A] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(0deg, #101D4A 0%, #873ED0 100%)' }}
            onClick={() => {
              setLoginModal({ show: true });
            }}
          >
            <span className="font-inter font-semibold text-white">
              Login
            </span>
            <img
              className="w-[15px] h-[15px]"
              src={IconDoorIn}
              alt="Wallet icon"
            />
          </button>
        ) : (
          <>
            {/* Mobile Version */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild className="md:hidden">
                <div
                  className="flex flex-row items-center py-2 px-3 border border-[#333333] rounded-full"
                  onClick={() => {
                    console.log('hello')
                  }}
                >
                  <img
                    className="w-[30px] h-[30px] rounded-full mr-3"
                    src={user?.profileImage}
                    alt="User avatar"
                  />
                  <img
                    className="w-6 h-6"
                    src={IconChevronDown}
                    alt="Chevron down"
                  />
                </div>
              </SheetTrigger>
              <SheetContent side="top" className="bg-black px-0 py-8 border-b-0 [&>button]:hidden">
                <nav className="flex flex-col gap-6 px-5">
                  <div className="flex flex-row items-center">
                    <img
                      className="w-12 h-12 rounded-full mr-3"
                      src={user?.profileImage}
                      alt="User avatar"
                    />
                    <div className="flex flex-col gap-2">
                      <span className="font-inter text-sm text-white font-medium">@{user?.username}</span>
                      <div className="flex flex-row items-center">
                        <img
                          className="w-5 h-5 mr-2"
                          src={IconWallet}
                          alt="Wallet icon"
                        />
                        <span className="font-inter text-white font-medium">{shortenAddress(primaryWallet?.publicKey || "")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-[1px] bg-white"/>
                  <Link
                    href={getXDaoHomePath()}
                    className="font-inter text-base text-white"
                    aria-label="Tweetonium Home"
                  >
                    X-DAO
                  </Link>
                  <button
                    className="text-start"
                    onClick={() => {
                      // Track wallet management sidebar open (mobile)
                      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                        label: 'open_wallet_management',
                        component: 'navbar',
                        location: 'mobile'
                      });
                      setSheetOpen(false);
                      showSidebarWalletManagement(true);
                    }}
                  >
                    <span className="font-inter text-white">My Wallet</span>
                  </button>
                  <button
                    className="text-start"
                    onClick={() => {
                      // Track account management open (mobile)
                      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                        label: 'open_account_management',
                        component: 'navbar',
                        location: 'mobile'
                      });
                      setSheetOpen(false);
                      setAccountManagementModal({ show: true });
                    }}
                  >
                    <span className="font-inter text-white">Account Management</span>
                  </button>
                  <button
                    className="text-start"
                    onClick={() => {
                      // Track logout confirm dialog open (mobile)
                      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                        label: 'open_logout_confirm',
                        component: 'navbar',
                        location: 'mobile'
                      });
                      setShowLogoutConfirm(true);
                    }}
                  >
                    <span className="font-inter text-white">Sign Out</span>
                  </button>
                </nav>
                <div className="flex flex-row justify-center mt-8">
                  <img
                    className="w-6 h-6"
                    src={IconChevronUp}
                    alt="Chevron down"
                    onClick={() => setSheetOpen(false)}
                  />
                </div>
              </SheetContent>
            </Sheet>

            {/* Desktop and Tablet Version */}
            <div className="md:flex hidden items-center space-x-2 border border-gray-800 rounded-full px-3 py-1.5">
              {/* Wallet Address - Opens Wallet Details */}
              <button
                className="text-sm hover:underline"
                onClick={() => {
                  // Track wallet management sidebar open (desktop)
                  trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                    label: 'open_wallet_management',
                    component: 'navbar',
                    location: 'desktop'
                  });
                  showSidebarWalletManagement(true);
                }}
              >
                {shortenAddress(primaryWallet?.publicKey || "")}
              </button>
              <span className="text-gray-400">|</span>
              
              {/* Twitter Username - Opens Dropdown Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-sm hover:underline">
                    @{user?.username}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-secondary border border-gray-800 text-white">
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                    onClick={() => {
                      // Track account management open (desktop)
                      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                        label: 'open_account_management',
                        component: 'navbar',
                        location: 'desktop'
                      });
                      setAccountManagementModal({ show: true });
                    }}
                  >
                    <User className="mr-2 h-4 w-4" />
                    <span>Account Management</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800"
                    onClick={() => {
                      // Track logout confirm dialog open (desktop)
                      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                        label: 'open_logout_confirm',
                        component: 'navbar',
                        location: 'desktop'
                      });
                      setShowLogoutConfirm(true);
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Wallet Details Dialog */}
            <Dialog open={showWalletDetails} onOpenChange={setShowWalletDetails}>
              <DialogContent className="w-[90%] max-w-md mx-auto bg-secondary border border-gray-800 text-white p-4 sm:p-6">
                <DialogClose className="absolute right-3 top-3 text-gray-400 hover:text-white">
                  <X className="h-5 w-5" />
                </DialogClose>
                <DialogHeader>
                  <DialogTitle className="text-xl sm:text-2xl font-medium mb-2">Wallet Details</DialogTitle>
                  <DialogDescription className="text-gray-300 text-sm sm:text-base">
                    Your Solana wallet on the Devnet network.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="py-4">
                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-sm text-gray-400 mb-1">Wallet Address</h4>
                    <div className="flex flex-col sm:flex-row sm:items-center bg-black p-2 sm:p-3 rounded-lg border border-gray-800">
                      <span className="text-xs sm:text-sm break-all mb-2 sm:mb-0">{primaryWallet?.publicKey}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-auto text-gray-400 hover:text-white"
                        onClick={() => copyWalletAddress(primaryWallet?.publicKey || '')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-sm text-gray-400 mb-1">Network</h4>
                    <p className="text-xs sm:text-sm bg-black p-2 sm:p-3 rounded-lg border border-gray-800">Solana Devnet</p>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm text-gray-400 mb-1">Balance</h4>
                    <WalletBalance publicKey={primaryWallet?.publicKey || ""} />
                  </div>

                  <Button 
                    variant="outline" 
                    className="w-full mt-4"
                    onClick={openInSolanaExplorer}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    <span className="text-sm">View on Solana Explorer</span>
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Logout Confirmation Dialog */}
            <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
              <AlertDialogContent className="w-[90%] max-w-md mx-auto bg-secondary border border-gray-800 text-white p-4 sm:p-6">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-xl sm:text-2xl font-medium">Are you sure you want to sign out?</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-4 pt-4">
                  <AlertDialogCancel
                    className="w-full sm:w-auto bg-transparent border border-gray-700 text-white hover:bg-gray-800 hover:text-white"
                    onClick={() => {
                      // Track logout cancellation
                      trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
                        label: 'logout_cancel',
                        component: 'navbar',
                        location: 'logout_dialog'
                      });
                    }}
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="w-full sm:w-auto bg-red-600 text-white hover:bg-red-700"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Account Management Modal */}
            <AccountManagementModal />
          </>
        )}
      </div>
    </nav>
  );
}
